import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import { data as routerData } from 'react-router';
import { getArticleCategoryItems } from '~/modules/articles/api/get-article-category-items.server';
import { getArticleNavigationItems } from '~/modules/articles/api/get-archizeen-common.server';
import { alsoInCategoryId } from '@modules/articles-revamp/util/also-in-options';
import { ALSO_IN_FILTER_KEY } from '@modules-rocco/articles/config/article-facets';
import { mapArchizeenTileToEditorial } from '@modules/articles-revamp/util/map-archizeen-tile-to-editorial';
import type { EditorialTileVariant } from '@modules/articles-revamp/util/map-archizeen-tile-to-editorial';

export const ARTICLES_LOAD_MORE_API_ROUTE = '/remix-api/articles/load-more';

/** Max tiles per request — guards against a manipulated `limit`. */
const MAX_LIMIT = 100;

/** Guard against a hand-crafted link pointing the loader off the directory. */
const isArticleCategoryLink = (link: string): boolean =>
    /^\/articles(\/[\w-]+)*$/.test(link) && !link.includes('..');

const EMPTY = { tiles: [], hasMore: false, totalCount: 0 };

/**
 * What the editorial grids send through the shared View More hook's `filters`
 * slot. The hook forwards it verbatim, so this is the only channel for anything
 * the directory contract has no field of its own for.
 */
interface EditorialFilters {
    /** The listing's facet query string, e.g. `?published-within=year`. */
    query?: string;
    /** Which editorial card the page draws; the two grids differ. */
    variant?: EditorialTileVariant;
    /** Articles already promoted above the grid, by link. */
    exclude?: string[];
}

const parseFilters = (raw: string | null): EditorialFilters => {
    if (!raw) return {};
    try {
        const parsed: unknown = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null
            ? (parsed as EditorialFilters)
            : {};
    } catch {
        return {};
    }
};

/**
 * A further window of an article listing, for the editorial grids' View More
 * and infinite scroll.
 *
 * Answers the same contract as the directory action so the grids can run on the
 * shared `useDirectoryViewMore` hook. Articles are not one of that action's
 * entity types — the listing has no search index behind it — so they are served
 * here and the hook is pointed at this route instead.
 *
 * Re-enters the page loader's own fetcher rather than adding a second query:
 * `getArticleCategoryItems` already reads the facets off the URL, so paging
 * here stays identical to paging by deep link.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const categoryLink = url.searchParams.get('categoryLink') ?? '';
    const offsetParam = url.searchParams.get('offset');
    const limitParam = url.searchParams.get('limit');

    if (!isArticleCategoryLink(categoryLink)) {
        logger.warning('Invalid categoryLink in articles load-more', {
            categoryLink,
        });
        return routerData(EMPTY, { status: 400 });
    }

    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
    const limit = limitParam ? parseInt(limitParam, 10) : 36;
    if (
        !Number.isInteger(offset) ||
        offset < 0 ||
        !Number.isInteger(limit) ||
        limit < 1 ||
        limit > MAX_LIMIT
    ) {
        logger.warning('Invalid offset/limit in articles load-more', {
            offsetParam,
            limitParam,
        });
        return routerData(EMPTY, { status: 400 });
    }

    const { query, variant, exclude } = parseFilters(
        url.searchParams.get('filters'),
    );

    const pageUrl = new URL(categoryLink + (query ?? ''), url.origin);

    // The cross-category facet travels as a URL segment; only the nav tree can
    // say which category that is, and it is also what validates it.
    const alsoInSegment = pageUrl.searchParams.get(ALSO_IN_FILTER_KEY);

    try {
        const alsoInCategoryID = alsoInSegment
            ? alsoInCategoryId(
                  await getArticleNavigationItems(context),
                  pageUrl.pathname,
                  alsoInSegment,
              )
            : undefined;

        const result = await getArticleCategoryItems({
            ...context,
            request: new Request(pageUrl, { headers: request.headers }),
            alsoInCategoryID,
            window: { offset, limit },
        });

        const rows = result?.articles ?? [];
        const totalCount = result?.totalCount ?? 0;
        const excluded = new Set(exclude ?? []);
        const kept = rows.filter((tile) => !excluded.has(tile.link));

        return {
            tiles: kept.map((tile) => ({
                model: mapArchizeenTileToEditorial(tile, { variant }),
            })),
            hasMore: offset + rows.length < totalCount,
            totalCount,
            // Rows read, not tiles kept. Dropping a promoted article leaves the
            // two apart, and paging on the tile count would re-read the row it
            // stands in for on every subsequent page.
            nextOffset: offset + rows.length,
        };
    } catch (error) {
        logger.error('Error loading more articles', {
            error,
            categoryLink,
            offset,
            limit,
        });
        return routerData(EMPTY, { status: 500 });
    }
};

/** Never revalidate — this endpoint only serves explicit `fetcher.load` calls. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;
